import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { FeeStatusBadge, formatINR, PAYMENT_METHOD_LABELS } from "@/lib/fees";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, Wallet, Ban, Receipt } from "lucide-react";

const emptyInstallment = () => ({ key: Math.random().toString(36).slice(2), amount: "", due_date: "", waived: false, waived_reason: "" });

function installmentStatus(row) {
    // Client-side mirror of the backend's derived status, used only while a plan
    // is being edited before it's saved (post-save the server's own numbers,
    // including paid_amount, are shown instead).
    if (row.waived) return "waived";
    if (row.status) return row.status;
    return "pending";
}

function InstallmentsEditor({ installments, setInstallments }) {
    const update = (key, patch) => setInstallments(installments.map(i => i.key === key ? { ...i, ...patch } : i));
    const remove = (key) => setInstallments(installments.filter(i => i.key !== key));
    return (
        <div className="space-y-2">
            {installments.map(row => (
                <div key={row.key} className="flex flex-wrap items-center gap-2 border border-border rounded-xl p-2.5">
                    <Input type="number" placeholder="Amount" className="w-32" value={row.amount}
                        onChange={e => update(row.key, { amount: e.target.value })} data-testid={`installment-amount-${row.key}`} />
                    <Input type="date" className="w-40" value={row.due_date}
                        onChange={e => update(row.key, { due_date: e.target.value })} data-testid={`installment-due-${row.key}`} />
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Checkbox checked={row.waived} onCheckedChange={v => update(row.key, { waived: !!v })} />
                        Waived
                    </label>
                    {row.waived && (
                        <Input placeholder="Reason for waiving" className="flex-1 min-w-[160px]" value={row.waived_reason}
                            onChange={e => update(row.key, { waived_reason: e.target.value })} />
                    )}
                    {row.status && <FeeStatusBadge status={installmentStatus(row)} />}
                    <Button type="button" variant="ghost" size="icon" className="ml-auto" onClick={() => remove(row.key)}>
                        <Trash2 className="h-4 w-4 text-[#E11D48]" />
                    </Button>
                </div>
            ))}
            <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={() => setInstallments([...installments, emptyInstallment()])}>
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Add installment
            </Button>
        </div>
    );
}

function StructureCard({ structure, courses, onSaved, onVoid, onLogPayment, onVoidPayment }) {
    const [form, setForm] = useState({
        course_id: structure.course_id || "", label: structure.label || "",
        total_fee: structure.total_fee, discount_amount: structure.discount_amount || 0,
        discount_reason: structure.discount_reason || "",
    });
    const [installments, setInstallments] = useState(
        (structure.installments || []).map(i => ({
            key: i.id, id: i.id, amount: i.amount, due_date: i.due_date,
            waived: i.waived, waived_reason: i.waived_reason || "", status: i.status, paid_amount: i.paid_amount,
        }))
    );
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                course_id: form.course_id || null, label: form.label || null,
                total_fee: parseFloat(form.total_fee), discount_amount: parseFloat(form.discount_amount) || 0,
                discount_reason: form.discount_reason || null,
                installments: installments.map(i => ({
                    id: i.id, amount: parseFloat(i.amount), due_date: i.due_date,
                    waived: i.waived, waived_reason: i.waived ? i.waived_reason : null,
                })),
            };
            await api.put(`/fees/structures/${structure.id}`, payload);
            toast.success("Fee structure updated");
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not save changes");
        } finally { setSaving(false); }
    };

    const status = structure.overdue ? "overdue" : (structure.balance <= 0.01 ? "paid" : "pending");

    return (
        <Card className="mx-card" data-testid={`fee-structure-${structure.id}`}>
            <CardContent className="p-5 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <div className="font-display text-lg">{structure.label || courses.find(c => c.id === structure.course_id)?.title || "Fee record"}</div>
                        <FeeStatusBadge status={status} />
                    </div>
                    <Button variant="ghost" size="sm" className="text-[#E11D48] hover:text-[#E11D48]" onClick={() => onVoid(structure)} data-testid={`void-structure-${structure.id}`}>
                        <Ban className="h-3.5 w-3.5 mr-1.5" /> Void this fee record
                    </Button>
                </div>

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                        <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Net payable</div>
                        <div className="font-display text-xl mt-1">{formatINR(structure.net_payable)}</div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Paid</div>
                        <div className="font-display text-xl mt-1 text-[#16A34A]">{formatINR(structure.total_paid)}</div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Balance</div>
                        <div className={`font-display text-xl mt-1 ${structure.balance > 0 ? "text-[#E11D48]" : ""}`}>{formatINR(structure.balance)}</div>
                    </div>
                    <div className="flex items-end">
                        <Button className="mx-btn-primary w-full" onClick={() => onLogPayment(structure)} data-testid={`log-payment-${structure.id}`}>
                            <Wallet className="h-4 w-4 mr-2" /> Log payment
                        </Button>
                    </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 border-t border-border pt-4">
                    <div>
                        <Label>Course (optional)</Label>
                        <Select value={form.course_id || "NONE"} onValueChange={v => setForm({ ...form, course_id: v === "NONE" ? "" : v })}>
                            <SelectTrigger><SelectValue placeholder="No course" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="NONE">No course</SelectItem>
                                {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div><Label>Label</Label><Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="e.g. Main course fee" /></div>
                    <div><Label>Total fee (₹)</Label><Input type="number" value={form.total_fee} onChange={e => setForm({ ...form, total_fee: e.target.value })} /></div>
                    <div><Label>Discount (₹)</Label><Input type="number" value={form.discount_amount} onChange={e => setForm({ ...form, discount_amount: e.target.value })} /></div>
                    <div className="sm:col-span-2 lg:col-span-4"><Label>Discount reason</Label><Input value={form.discount_reason} onChange={e => setForm({ ...form, discount_reason: e.target.value })} placeholder="e.g. Early-bird, sibling discount" /></div>
                </div>

                <div>
                    <Label className="mb-2 block">Installment plan</Label>
                    <InstallmentsEditor installments={installments} setInstallments={setInstallments} />
                </div>

                <Button onClick={save} disabled={saving} className="mx-btn-primary" data-testid={`save-structure-${structure.id}`}>
                    Save changes
                </Button>

                {structure.payments.length > 0 && (
                    <div className="border-t border-border pt-4">
                        <Label className="mb-2 block flex items-center gap-1.5"><Receipt className="h-3.5 w-3.5" /> Payment history</Label>
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Amount</TableHead>
                                        <TableHead>Method</TableHead>
                                        <TableHead>Reference / note</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead className="w-12"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {structure.payments.map(p => (
                                        <TableRow key={p.id} className={p.voided ? "opacity-50" : ""} data-testid={`payment-${p.id}`}>
                                            <TableCell className="text-sm">{p.date}</TableCell>
                                            <TableCell className="text-sm font-semibold">{formatINR(p.amount)}</TableCell>
                                            <TableCell className="text-sm">{PAYMENT_METHOD_LABELS[p.method] || p.method}</TableCell>
                                            <TableCell className="text-xs text-muted-foreground">
                                                {p.reference || "—"}{p.note ? ` · ${p.note}` : ""}
                                                {p.voided && <div className="text-[#E11D48]">Voided: {p.void_reason}</div>}
                                            </TableCell>
                                            <TableCell>{p.voided ? <Badge variant="secondary">Voided</Badge> : <Badge className="bg-[#16A34A] hover:bg-[#16A34A]">Active</Badge>}</TableCell>
                                            <TableCell>
                                                {!p.voided && (
                                                    <Button variant="ghost" size="icon" onClick={() => onVoidPayment(p)} data-testid={`void-payment-${p.id}`}>
                                                        <Ban className="h-4 w-4 text-[#E11D48]" />
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default function FeeDetail() {
    const { studentId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [courses, setCourses] = useState([]);

    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState({ course_id: "", label: "", total_fee: "", discount_amount: "0", discount_reason: "" });
    const [createInstallments, setCreateInstallments] = useState([]);

    const [payFor, setPayFor] = useState(null); // structure
    const [payForm, setPayForm] = useState({ amount: "", date: "", method: "cash", reference: "", note: "" });
    const [allocations, setAllocations] = useState([]); // [{key, installment_id, amount}]

    const [voidTarget, setVoidTarget] = useState(null); // { kind: 'structure'|'payment', id, structureId? }
    const [voidReason, setVoidReason] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get(`/fees/students/${studentId}`);
            setData(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not load this student's fees");
        }
    };
    useEffect(() => { load(); }, [studentId]); // eslint-disable-line
    useEffect(() => { api.get("/courses").then(r => setCourses(r.data.items)).catch(() => {}); }, []);

    const createStructure = async (e) => {
        e.preventDefault();
        try {
            await api.post("/fees/structures", {
                student_id: studentId,
                course_id: createForm.course_id || null, label: createForm.label || null,
                total_fee: parseFloat(createForm.total_fee), discount_amount: parseFloat(createForm.discount_amount) || 0,
                discount_reason: createForm.discount_reason || null,
                installments: createInstallments.map(i => ({ amount: parseFloat(i.amount), due_date: i.due_date })),
            });
            toast.success("Fee structure created");
            setCreateOpen(false);
            setCreateForm({ course_id: "", label: "", total_fee: "", discount_amount: "0", discount_reason: "" });
            setCreateInstallments([]);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not create the fee structure");
        }
    };

    const openPay = (structure) => {
        setPayFor(structure);
        setPayForm({ amount: "", date: new Date().toISOString().slice(0, 10), method: "cash", reference: "", note: "" });
        setAllocations([]);
    };

    const allocatedTotal = allocations.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
    const payAmount = parseFloat(payForm.amount) || 0;

    const submitPayment = async () => {
        if (allocatedTotal - payAmount > 0.01) {
            toast.error("Allocated amount can't exceed the payment amount");
            return;
        }
        try {
            await api.post("/fees/payments", {
                fee_structure_id: payFor.id, amount: payAmount, date: payForm.date, method: payForm.method,
                reference: payForm.reference || null, note: payForm.note || null,
                allocations: allocations.filter(a => a.installment_id && parseFloat(a.amount) > 0)
                    .map(a => ({ installment_id: a.installment_id, amount: parseFloat(a.amount) })),
            });
            toast.success("Payment logged");
            setPayFor(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not log this payment");
        }
    };

    const confirmVoid = async () => {
        if (!voidReason.trim()) return;
        try {
            if (voidTarget.kind === "structure") {
                await api.post(`/fees/structures/${voidTarget.id}/void`, { reason: voidReason });
            } else {
                await api.post(`/fees/payments/${voidTarget.id}/void`, { reason: voidReason });
            }
            toast.success("Voided");
            setVoidTarget(null);
            setVoidReason("");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not void this");
        }
    };

    if (!data) return null;

    return (
        <div className="space-y-6" data-testid="fee-detail-page">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin/fees")} className="-ml-2">
                <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to batch report
            </Button>

            <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-brand-gradient flex items-center justify-center text-white font-bold text-lg">{data.student.name?.[0]}</div>
                    <div>
                        <h1 className="font-display text-2xl lg:text-3xl">{data.student.name}</h1>
                        <div className="text-xs text-muted-foreground font-mono">{data.student.enrollment_number} · {data.student.email}</div>
                    </div>
                </div>
                <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                    <DialogTrigger asChild>
                        <Button className="mx-btn-primary" data-testid="add-fee-structure-btn"><Plus className="h-4 w-4 mr-2" /> Add fee record</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>New fee record</DialogTitle>
                            <DialogDescription>A student may have more than one — e.g. their main course plus an elective.</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={createStructure} className="space-y-4">
                            <div className="grid gap-3 sm:grid-cols-2">
                                <div>
                                    <Label>Course (optional)</Label>
                                    <Select value={createForm.course_id || "NONE"} onValueChange={v => setCreateForm({ ...createForm, course_id: v === "NONE" ? "" : v })}>
                                        <SelectTrigger><SelectValue placeholder="No course" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="NONE">No course</SelectItem>
                                            {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div><Label>Label</Label><Input value={createForm.label} onChange={e => setCreateForm({ ...createForm, label: e.target.value })} placeholder="e.g. Main course fee" /></div>
                                <div><Label>Total fee (₹)</Label><Input required type="number" value={createForm.total_fee} onChange={e => setCreateForm({ ...createForm, total_fee: e.target.value })} data-testid="create-total-fee" /></div>
                                <div><Label>Discount (₹)</Label><Input type="number" value={createForm.discount_amount} onChange={e => setCreateForm({ ...createForm, discount_amount: e.target.value })} /></div>
                                <div className="sm:col-span-2"><Label>Discount reason</Label><Input value={createForm.discount_reason} onChange={e => setCreateForm({ ...createForm, discount_reason: e.target.value })} /></div>
                            </div>
                            <div>
                                <Label className="mb-2 block">Installment plan (optional)</Label>
                                <InstallmentsEditor installments={createInstallments} setInstallments={setCreateInstallments} />
                            </div>
                            <DialogFooter>
                                <Button type="submit" className="mx-btn-primary" data-testid="create-structure-submit">Create fee record</Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {data.fee_structures.length > 0 && (
                <Card className="mx-card">
                    <CardContent className="p-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <div>
                            <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Total net payable</div>
                            <div className="font-display text-2xl mt-1">{formatINR(data.summary.total_net_payable)}</div>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Total paid</div>
                            <div className="font-display text-2xl mt-1 text-[#16A34A]">{formatINR(data.summary.total_paid)}</div>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Total balance</div>
                            <div className={`font-display text-2xl mt-1 ${data.summary.total_balance > 0 ? "text-[#E11D48]" : ""}`}>{formatINR(data.summary.total_balance)}</div>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Overdue</div>
                            <div className="mt-1">{data.summary.overdue ? <Badge className="bg-[#E11D48] hover:bg-[#E11D48]">Yes</Badge> : <Badge variant="secondary">No</Badge>}</div>
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="space-y-4">
                {data.fee_structures.map(s => (
                    <StructureCard key={s.id} structure={s} courses={courses} onSaved={load}
                        onVoid={(structure) => { setVoidTarget({ kind: "structure", id: structure.id }); setVoidReason(""); }}
                        onLogPayment={openPay}
                        onVoidPayment={(payment) => { setVoidTarget({ kind: "payment", id: payment.id }); setVoidReason(""); }} />
                ))}
                {data.fee_structures.length === 0 && (
                    <div className="mx-card p-12 text-center text-muted-foreground">No fee record yet for this student. Use “Add fee record” to set one up.</div>
                )}
            </div>

            {/* Log payment */}
            <Dialog open={!!payFor} onOpenChange={v => { if (!v) setPayFor(null); }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Log a payment</DialogTitle>
                        <DialogDescription>Record money received outside the system (UPI/bank/cash/cheque). Nothing moves through this portal.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Amount (₹)</Label><Input type="number" value={payForm.amount} onChange={e => setPayForm({ ...payForm, amount: e.target.value })} data-testid="payment-amount-input" /></div>
                            <div><Label>Date</Label><Input type="date" value={payForm.date} onChange={e => setPayForm({ ...payForm, date: e.target.value })} data-testid="payment-date-input" /></div>
                            <div>
                                <Label>Method</Label>
                                <Select value={payForm.method} onValueChange={v => setPayForm({ ...payForm, method: v })}>
                                    <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(PAYMENT_METHOD_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div><Label>Reference</Label><Input value={payForm.reference} onChange={e => setPayForm({ ...payForm, reference: e.target.value })} placeholder="UTR / txn id" /></div>
                        </div>
                        <div><Label>Note</Label><Input value={payForm.note} onChange={e => setPayForm({ ...payForm, note: e.target.value })} /></div>

                        <div>
                            <Label className="mb-2 block">Apply to installment(s) — optional, can split across several</Label>
                            <div className="space-y-2">
                                {allocations.map((a, idx) => (
                                    <div key={idx} className="flex gap-2 items-center">
                                        <Select value={a.installment_id} onValueChange={v => setAllocations(allocations.map((x, i) => i === idx ? { ...x, installment_id: v } : x))}>
                                            <SelectTrigger className="flex-1"><SelectValue placeholder="Installment" /></SelectTrigger>
                                            <SelectContent>
                                                {(payFor?.installments || []).map(i => (
                                                    <SelectItem key={i.id} value={i.id}>{i.due_date} · {formatINR(i.amount)} ({i.status})</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <Input type="number" className="w-28" placeholder="Amount" value={a.amount}
                                            onChange={e => setAllocations(allocations.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x))} />
                                        <Button type="button" variant="ghost" size="icon" onClick={() => setAllocations(allocations.filter((_, i) => i !== idx))}>
                                            <Trash2 className="h-4 w-4 text-[#E11D48]" />
                                        </Button>
                                    </div>
                                ))}
                                <Button type="button" variant="outline" size="sm" className="rounded-xl"
                                    onClick={() => setAllocations([...allocations, { installment_id: "", amount: "" }])}>
                                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Add allocation
                                </Button>
                            </div>
                            <div className={`text-xs mt-2 ${allocatedTotal > payAmount ? "text-[#E11D48]" : "text-muted-foreground"}`}>
                                Allocated {formatINR(allocatedTotal)} of {formatINR(payAmount)}
                                {allocatedTotal < payAmount && payAmount > 0 && ` · ${formatINR(payAmount - allocatedTotal)} unallocated`}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={submitPayment} disabled={!payAmount || !payForm.date} className="mx-btn-primary" data-testid="submit-payment-btn">
                            Log payment
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Void (reason required) */}
            <Dialog open={!!voidTarget} onOpenChange={v => { if (!v) setVoidTarget(null); }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Void this {voidTarget?.kind === "structure" ? "fee record" : "payment"}?</DialogTitle>
                        <DialogDescription>
                            It is never deleted — it stays in the audit history marked voided, and is excluded from balance calculations from now on. A reason is required.
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <Label>Reason</Label>
                        <Textarea rows={3} value={voidReason} onChange={e => setVoidReason(e.target.value)}
                            placeholder="e.g. entered against the wrong student, bounced UPI, duplicate entry" data-testid="void-reason-input" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-xl" onClick={() => setVoidTarget(null)}>Keep it</Button>
                        <Button onClick={confirmVoid} disabled={!voidReason.trim()} className="bg-[#E11D48] hover:bg-[#E11D48] text-white rounded-xl" data-testid="confirm-void-btn">
                            Void it
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
